import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { FloorMesh } from '../model/types'
import {
  MODEL_PATH,
  DRAG_THRESHOLD,
  FLOOR_COLOR,
  HIGHLIGHT_COLOR,
} from '../model/constants'
import { enhanceBuildingMaterials, isFloorMesh } from './mesh-utils'

interface BuildingModelProps {
  onFloorClick: (floor: FloorMesh) => void
}

const BuildingModel: React.FC<BuildingModelProps> = ({ onFloorClick }) => {
  const { scene } = useGLTF(MODEL_PATH)
  const model = useMemo(() => {
    const cloned = scene.clone(true)
    enhanceBuildingMaterials(cloned)
    return cloned
  }, [scene])
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null)
  const [hoveredUuid, setHoveredUuid] = useState<string | null>(null)

  useEffect(() => {
    model.traverse(child => {
      if (!(child instanceof THREE.Mesh)) return

      if (isFloorMesh(child)) {
        child.material = new THREE.MeshStandardMaterial({
          color: FLOOR_COLOR,
          transparent: true,
          opacity: 0,
        })
        return
      }

      // Стены и прочая геометрия не должны перехватывать наведение на этажи
      child.raycast = () => {}
    })
  }, [model])

  useEffect(() => {
    model.traverse(child => {
      if (!isFloorMesh(child)) return

      const material = child.material as THREE.MeshStandardMaterial
      const isHovered = child.uuid === hoveredUuid
      material.color.copy(isHovered ? HIGHLIGHT_COLOR : FLOOR_COLOR)
      material.opacity = isHovered ? 0.5 : 0
      material.needsUpdate = true
    })
  }, [hoveredUuid, model])

  const handlePointerOver = useCallback((e: any) => {
    e.stopPropagation()
    if (isFloorMesh(e.object)) {
      document.body.style.cursor = 'pointer'
      setHoveredUuid(e.object.uuid)
    }
  }, [])

  const handlePointerOut = useCallback((e: any) => {
    e.stopPropagation()
    if (isFloorMesh(e.object)) {
      document.body.style.cursor = 'auto'
      setHoveredUuid(null)
    }
  }, [])

  const handlePointerDown = useCallback((e: any) => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY }
  }, [])

  const handleClick = useCallback(
    (e: any) => {
      e.stopPropagation()
      if (!pointerDownPos.current) return

      const dx = e.clientX - pointerDownPos.current.x
      const dy = e.clientY - pointerDownPos.current.y
      const wasDrag = Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD
      pointerDownPos.current = null

      if (wasDrag) return
      if (isFloorMesh(e.object)) {
        onFloorClick({ name: e.object.name, uuid: e.object.uuid })
      }
    },
    [onFloorClick],
  )

  return (
    <primitive
      object={model}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    />
  )
}

export default BuildingModel
